/**
 * Phaser 애니메이션 등록.
 * 프레임 수는 AssetTable 이 해석한 값을 쓰므로, 사용자가 프레임을 더 많은 스프라이트로
 * 교체해도 애니메이션이 자동으로 맞춰진다.
 */

import type Phaser from 'phaser';
import { BASIC_ATTACK } from '../data/balance';
import { SKILL_CATALOG } from '../data/skills';
import { MONSTERS } from '../data/monsters';
import type { AssetTable } from './loader';

export const ANIM = {
  stand: 'anim-stand',
  walk: 'anim-walk',
  jump: 'anim-jump',
  hurt: 'anim-hurt',
  attack: 'anim-attack',
  attackStrong: 'anim-attack-strong',
  swing: 'anim-swing',
  ladder: 'anim-ladder',
  rope: 'anim-rope',
  portal: 'anim-portal',
  hit: 'anim-fx-hit',
  slash: 'anim-fx-slash',
  slashStrong: 'anim-fx-slash-strong',
  blast: 'anim-fx-blast',
  levelUp: 'anim-fx-level-up',
  spore: 'anim-fx-spore',
  bolt: 'anim-fx-bolt',
  arrow: 'anim-fx-arrow',
  star: 'anim-fx-star',
} as const;

export function monsterAnim(textureKey: string): string {
  return `anim-${textureKey}`;
}

interface AnimSpec {
  key: string;
  texture: string;
  repeat: number;
  /** 전체 재생 시간 (ms). frameRate 대신 duration 을 쓰면 프레임 수가 달라도 길이가 유지된다 */
  duration: number;
  yoyo?: boolean;
}

export function registerAnimations(scene: Phaser.Scene, assets: AssetTable): void {
  const powerStrike = SKILL_CATALOG.powerStrike;
  const slashBlast = SKILL_CATALOG.slashBlast;

  const specs: AnimSpec[] = [
    { key: ANIM.stand, texture: 'player-stand', repeat: -1, duration: 900 },
    { key: ANIM.walk, texture: 'player-walk', repeat: -1, duration: 480 },
    { key: ANIM.jump, texture: 'player-jump', repeat: -1, duration: 400 },
    { key: ANIM.hurt, texture: 'player-hurt', repeat: -1, duration: 400 },
    { key: ANIM.attack, texture: 'player-attack', repeat: 0, duration: BASIC_ATTACK.castMs },
    {
      key: ANIM.attackStrong,
      texture: 'player-attack',
      repeat: 0,
      duration: powerStrike?.castMs ?? 360,
    },
    { key: ANIM.swing, texture: 'player-swing', repeat: 0, duration: slashBlast?.castMs ?? 420 },
    { key: ANIM.ladder, texture: 'player-ladder', repeat: -1, duration: 520 },
    { key: ANIM.rope, texture: 'player-rope', repeat: -1, duration: 520 },
    { key: ANIM.portal, texture: 'portal', repeat: -1, duration: 620 },
    { key: ANIM.hit, texture: 'fx-hit', repeat: 0, duration: 170 },
    { key: ANIM.slash, texture: 'fx-slash', repeat: 0, duration: 150 },
    { key: ANIM.slashStrong, texture: 'fx-slash-strong', repeat: 0, duration: 190 },
    { key: ANIM.blast, texture: 'fx-blast', repeat: 0, duration: 230 },
    { key: ANIM.levelUp, texture: 'fx-level-up', repeat: 1, duration: 700 },
    { key: ANIM.spore, texture: 'fx-spore', repeat: -1, duration: 280 },
    { key: ANIM.bolt, texture: 'fx-bolt', repeat: -1, duration: 220 },
    { key: ANIM.arrow, texture: 'fx-arrow', repeat: -1, duration: 180 },
    { key: ANIM.star, texture: 'fx-star', repeat: -1, duration: 200 },
  ];

  for (const spec of specs) {
    if (scene.anims.exists(spec.key)) scene.anims.remove(spec.key);
    const count = assets.frameCount(spec.texture);
    scene.anims.create({
      key: spec.key,
      frames: scene.anims.generateFrameNumbers(spec.texture, {
        start: 0,
        end: Math.max(0, count - 1),
      }),
      duration: spec.duration,
      repeat: spec.repeat,
      yoyo: spec.yoyo ?? false,
    });
  }

  for (const def of Object.values(MONSTERS)) {
    const key = monsterAnim(def.texture);
    if (scene.anims.exists(key)) continue;
    const count = assets.frameCount(def.texture);
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(def.texture, {
        start: 0,
        end: Math.max(0, count - 1),
      }),
      duration: def.moveStyle === 'hop' ? 520 : 700,
      repeat: -1,
      yoyo: true,
    });
  }
}
